import { useState, useEffect, useCallback } from 'react';
import { getTokenStats } from '../lib/api';
import { ShareToTelegram } from './ShareToTelegram';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { AddressBadge } from './AddressBadge';
import { TrendingUp, TrendingDown, ArrowUpDown, Users, Trophy, Activity, BarChart3, AlertCircle } from 'lucide-react';
import { formatNumber } from '../lib/utils';

const WINDOWS = [
  { id: '1h', label: '1H' },
  { id: '6h', label: '6H' },
  { id: '12h', label: '12H' },
  { id: '24h', label: '24H' },
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' },
];

function fmtUsd(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}
function fmtTok(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(2);
}

export function TokenStats({ tokenAddress, symbol }) {
  const [window, setWindow] = useState('24h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getTokenStats(tokenAddress, window);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tokenAddress, window]);

  useEffect(() => { fetch(); }, [fetch]);

  const s = data?.swaps;
  const t = data?.transfers;
  const sym = data?.symbol || symbol || '?';
  const netPos = (s?.net_flow_usd ?? 0) >= 0;
  const buyPct = s && (s.buys_count + s.sells_count) > 0
    ? (s.buys_count / (s.buys_count + s.sells_count)) * 100
    : 50;

  return (
    <Card className="border-border/70" data-testid="token-stats-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" /> Token Stats ({sym})
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border bg-card overflow-hidden" data-testid="token-stats-window-picker">
              {WINDOWS.map(w => (
                <Button
                  key={w.id}
                  size="sm"
                  variant="ghost"
                  className={`rounded-none border-0 px-3 text-xs h-8 ${window === w.id ? 'bg-amber-600/20 text-amber-400 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setWindow(w.id)}
                  data-testid={`window-${w.id}-btn`}
                >{w.label}</Button>
              ))}
            </div>
            <ShareToTelegram kind="tok" payload={tokenAddress} label="Share" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading || !data ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !s?.is_indexed ? (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2 text-xs">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-300 font-medium">DEX trading not indexed</p>
                <p className="text-muted-foreground mt-1">
                  Showing on-chain transfer stats only. To track DEX swaps for this token,
                  add a pool via the Telegram bot (<code className="text-amber-400">/quickadd</code> or Tokens → ➕ Add Token).
                </p>
              </div>
            </div>
            <TransferStrip t={t} sym={sym} />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Top KPI tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-kpis">
              <KpiTile
                testid="kpi-volume"
                icon={<ArrowUpDown className="w-4 h-4 text-blue-400" />}
                label="Volume"
                value={fmtUsd(s.volume_usd)}
                sub={`${(s.trades_count || 0).toLocaleString()} trades`}
              />
              <KpiTile
                testid="kpi-buys"
                icon={<TrendingUp className="w-4 h-4 text-emerald-400" />}
                label="Buys"
                value={fmtUsd(s.buys_usd)}
                sub={`${(s.buys_count || 0).toLocaleString()} (${fmtTok(s.buys_token)} ${sym})`}
                accent="emerald"
              />
              <KpiTile
                testid="kpi-sells"
                icon={<TrendingDown className="w-4 h-4 text-rose-400" />}
                label="Sells"
                value={fmtUsd(s.sells_usd)}
                sub={`${(s.sells_count || 0).toLocaleString()} (${fmtTok(s.sells_token)} ${sym})`}
                accent="rose"
              />
              <KpiTile
                testid="kpi-net-flow"
                icon={netPos ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-rose-400" />}
                label="Net Flow"
                value={`${netPos ? '+' : ''}${fmtUsd(s.net_flow_usd)}`}
                sub={`${netPos ? '+' : ''}${fmtTok(s.net_flow_token)} ${sym}`}
                accent={netPos ? 'emerald' : 'rose'}
              />
            </div>

            {/* Buy/Sell pressure bar */}
            <div data-testid="buy-sell-pressure">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-emerald-400 font-mono">🟢 BUY {buyPct.toFixed(1)}%</span>
                <span className="text-muted-foreground">Pressure</span>
                <span className="text-rose-400 font-mono">🔴 SELL {(100 - buyPct).toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-rose-500/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${buyPct}%` }}
                />
              </div>
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SubTile
                testid="sub-unique-traders"
                icon={<Users className="w-4 h-4 text-violet-400" />}
                label="Unique Traders"
                value={(s.unique_traders || 0).toLocaleString()}
              />
              <SubTile
                testid="sub-transfer-count"
                icon={<Activity className="w-4 h-4 text-amber-400" />}
                label="Transfers"
                value={(t.transfer_count || 0).toLocaleString()}
                sub={`${(t.txn_count || 0).toLocaleString()} txs`}
              />
              <SubTile
                testid="sub-transfer-volume"
                icon={<ArrowUpDown className="w-4 h-4 text-cyan-400" />}
                label={`Moved (${sym})`}
                value={fmtTok(t.volume_token)}
              />
              <SubTile
                testid="sub-unique-wallets"
                icon={<Users className="w-4 h-4 text-blue-400" />}
                label="Unique Wallets"
                value={(t.unique_wallets || 0).toLocaleString()}
              />
            </div>

            {/* Top buyers / sellers */}
            <div className="grid lg:grid-cols-2 gap-3">
              <TopList
                title="Top Buyers"
                color="emerald"
                rows={s.top_buyers || []}
                sym={sym}
                testid="top-buyers"
              />
              <TopList
                title="Top Sellers"
                color="rose"
                rows={s.top_sellers || []}
                sym={sym}
                testid="top-sellers"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({ icon, label, value, sub, accent, testid }) {
  const colorClass = accent === 'emerald'
    ? 'text-emerald-400'
    : accent === 'rose'
      ? 'text-rose-400'
      : 'text-foreground';
  return (
    <div className="bg-muted/30 border border-border/60 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">{icon}<span>{label}</span></div>
      <p className={`text-xl font-mono font-bold ${colorClass}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function SubTile({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-muted/20 border border-border/40 rounded-lg p-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{icon}<span>{label}</span></div>
      <p className="text-base font-mono font-semibold mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function TopList({ title, color, rows, sym, testid }) {
  const accent = color === 'emerald' ? 'text-emerald-400' : 'text-rose-400';
  const bgAccent = color === 'emerald' ? 'bg-emerald-500/10' : 'bg-rose-500/10';
  return (
    <div className={`rounded-lg border border-border/60 ${bgAccent} p-3`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-xs font-semibold mb-2 ${accent}`}>
        <Trophy className="w-3.5 h-3.5" /> {title}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No activity in this window.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={`${testid}-${r.wallet}`} className="flex items-center justify-between text-xs" data-testid={`${testid}-row-${i}`}>
              <div className="flex items-center gap-1.5 min-w-0">
                <Badge variant="outline" className="text-[10px] px-1.5 border-border">#{i + 1}</Badge>
                <AddressBadge address={r.wallet} truncate />
              </div>
              <div className="text-right font-mono">
                <div className={accent}>{fmtUsd(r.usd)}</div>
                <div className="text-[10px] text-muted-foreground">{r.count} {r.count === 1 ? 'trade' : 'trades'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransferStrip({ t, sym }) {
  if (!t) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <SubTile
        testid="sub-transfer-count"
        icon={<Activity className="w-4 h-4 text-amber-400" />}
        label="Transfers"
        value={(t.transfer_count || 0).toLocaleString()}
        sub={`${(t.txn_count || 0).toLocaleString()} txs`}
      />
      <SubTile
        testid="sub-transfer-volume"
        icon={<ArrowUpDown className="w-4 h-4 text-cyan-400" />}
        label={`Moved (${sym})`}
        value={fmtTok(t.volume_token)}
      />
      <SubTile
        testid="sub-unique-wallets"
        icon={<Users className="w-4 h-4 text-blue-400" />}
        label="Unique Wallets"
        value={(t.unique_wallets || 0).toLocaleString()}
      />
    </div>
  );
}
